import {
  getAgeCategory,
  getFinalHairClass,
  normalizeYesNo,
  toFlatList,
} from './chatAnswerHelpers'



const firstAvailable = (...values) => {
  for (const value of values) {
    if (value === undefined || value === null || value === '') continue

    if (Array.isArray(value) && value.length === 0) continue

    return value
  }

  return null
}

const getReturningUserHairClass = (answersMap, agentData) => {
  return firstAvailable(
    getFinalHairClass(answersMap),

    answersMap?.hair_type,
    answersMap?.hair_class,

    agentData?.hair_type,
    agentData?.hair_class,
    agentData?.hairClass,
  )
}

const getMergedHairProfile = (answersMap, agentData) => {
  const todaysHairProfile = answersMap?.hairProfile || {}

  return {
    hair_state: firstAvailable(
      todaysHairProfile?.hair_state,
      answersMap?.hair_state,
      agentData?.hair_state,
      'Natural',
    ),

    hair_goals: firstAvailable(
      todaysHairProfile?.hair_goals,
      answersMap?.hair_goals,
      agentData?.hair_goals,
      'None',
    ),

    hair_challenges: firstAvailable(
      todaysHairProfile?.hair_challenges,
      answersMap?.hair_challenges,
      agentData?.hair_challenges,
      'None',
    ),

    scalp_challenges: firstAvailable(
      todaysHairProfile?.scalp_challenges,
      answersMap?.scalp_challenges,
      agentData?.scalp_challenges,
      'None',
    ),
  }
}

const getMergedMedicalContext = (answersMap, agentData) => {
  const todaysMedical = answersMap?.medicalLifestyle || {}

  /*
   * Existing-user API currently gives us one combined historical field:
   *
   * allergy_or_medical: ['No']
   *
   * Today's refreshed flow gives us the richer structured object.
   * So today's value should always win.
   */
  const allergyOrMedical = firstAvailable(
    todaysMedical?.allergies_or_sensitivities,
    todaysMedical?.medical_conditions,

    answersMap?.allergies_or_sensitivities,
    answersMap?.medical_conditions,

    agentData?.allergy_or_medical,
    'No',
  )

  const pregnancyOrPostpartum = firstAvailable(
    todaysMedical?.pregnancy_or_postpartum,
    answersMap?.pregnancy_or_postpartum,
    agentData?.pregnancy_or_postpartum,
    'No',
  )

  return {
    allergyOrMedical,
    pregnancyOrPostpartum,
  }
}

const getMergedDemographics = (answersMap, agentData) => {
  const todaysDemographics = answersMap?.demographics || {}

  const age = firstAvailable(
    todaysDemographics?.age,
    answersMap?.age,
  )

  const savedAgeCategory = firstAvailable(
    agentData?.age_category,
  )

  return {
    ageCategory:
      getAgeCategory(age) ||
      firstAvailable(savedAgeCategory, '18-24 years'),

    gender: firstAvailable(
      todaysDemographics?.gender,
      answersMap?.gender,
      agentData?.gender,
      'Female',
    ),

    residence: firstAvailable(
      agentData?.residence,
      'City',
    ),

    placeName: firstAvailable(
      todaysDemographics?.location,
      answersMap?.location,
      agentData?.place_name,
      'City',
    ),
  }
}



const firstDefined = (...values) => {
  return values.find(
    (value) =>
      value !== undefined &&
      value !== null &&
      value !== '',
  )
}

const getSavedHairClass = (agentData = {}) =>
  firstDefined(
    agentData.hair_type,
    agentData.hair_class,
    agentData.hairClass,
  )

const getCurrentHairClass = (answersMap = {}, agentData = {}) =>
  firstDefined(
    getFinalHairClass(answersMap),
    getSavedHairClass(agentData),
  )

const getCurrentHairProfile = (answersMap = {}, agentData = {}) => {
  const todaysProfile = answersMap.hairProfile || {}
  const savedProfile = agentData.hairProfile || {}

  return {
    hair_state: firstDefined(
      todaysProfile.hair_state,
      answersMap.hair_state,
      savedProfile.hair_state,
      agentData.hair_state,
      'Natural',
    ),

    hair_goals: firstDefined(
      todaysProfile.hair_goals,
      answersMap.hair_goals,
      savedProfile.hair_goals,
      agentData.hair_goals,
      'None',
    ),

    hair_challenges: firstDefined(
      todaysProfile.hair_challenges,
      answersMap.hair_challenges,
      savedProfile.hair_challenges,
      agentData.hair_challenges,
      'None',
    ),

    scalp_challenges: firstDefined(
      todaysProfile.scalp_challenges,
      answersMap.scalp_challenges,
      savedProfile.scalp_challenges,
      agentData.scalp_challenges,
      'None',
    ),
  }
}

const getCurrentMedicalLifestyle = (answersMap = {}, agentData = {}) => {
  const todaysMedical = answersMap.medicalLifestyle || {}
  const savedMedical = agentData.medicalLifestyle || {}

  return {
    allergies_or_sensitivities: firstDefined(
      todaysMedical.allergies_or_sensitivities,
      answersMap.allergies_or_sensitivities,
      savedMedical.allergies_or_sensitivities,
      agentData.allergies_or_sensitivities,
      'No',
    ),

    pregnancy_or_postpartum: firstDefined(
      todaysMedical.pregnancy_or_postpartum,
      answersMap.pregnancy_or_postpartum,
      savedMedical.pregnancy_or_postpartum,
      agentData.pregnancy_or_postpartum,
      'No',
    ),

    medical_conditions: firstDefined(
      todaysMedical.medical_conditions,
      answersMap.medical_conditions,
      savedMedical.medical_conditions,
      agentData.medical_conditions,
      'No',
    ),

    lifestyle_factors: firstDefined(
      todaysMedical.lifestyle_factors,
      answersMap.lifestyle_factors,
      savedMedical.lifestyle_factors,
      agentData.lifestyle_factors,
      'None',
    ),

    medications_or_treatments: firstDefined(
      todaysMedical.medications_or_treatments,
      answersMap.medications_or_treatments,
      savedMedical.medications_or_treatments,
      agentData.medications_or_treatments,
      'None',
    ),
  }
}

const getCurrentDemographics = (answersMap = {}, agentData = {}) => {
  const todaysDemographics = answersMap.demographics || {}

  const age = firstDefined(
    todaysDemographics.age,
    answersMap.age,
    agentData.age,
  )

  const savedAgeCategory = firstDefined(
    agentData.age_category,
    agentData.ageCategory,
  )

  return {
    age,
    ageCategory:
      getAgeCategory(age) ||
      savedAgeCategory ||
      '18-24 years',

    gender: firstDefined(
      todaysDemographics.gender,
      answersMap.gender,
      agentData.gender,
      'Female',
    ),

    location: firstDefined(
      todaysDemographics.location,
      answersMap.location,
      agentData.location,
      agentData.city,
      'City',
    ),
  }
}



export const buildRecommendationPayload = ({
  answersMap,
  collectedAnswers,
  agentData,
  storeId,
  agentId,
  language
}) => {
    const normalizedLanguage = String(
    language || 'EN',
  )
    .trim()
    .toLowerCase()

  const apiLanguage = [
    'en',
    'fr',
    'nl',
    'sw',
  ].includes(normalizedLanguage)
    ? normalizedLanguage
    : 'en'
  const isGuestFlow =
    answersMap?.existingUserAction === 'option_someone_else'

  const finalHairClass = isGuestFlow
    ? getFinalHairClass(answersMap)
    : getReturningUserHairClass(
        answersMap,
        agentData,
      )

  const hairProfile = isGuestFlow
    ? {
        hair_state:
          answersMap?.hairProfile?.hair_state ||
          answersMap?.hair_state ||
          'Natural',

        hair_goals:
          answersMap?.hairProfile?.hair_goals ||
          answersMap?.hair_goals ||
          'None',

        hair_challenges:
          answersMap?.hairProfile?.hair_challenges ||
          answersMap?.hair_challenges ||
          'None',

        scalp_challenges:
          answersMap?.hairProfile?.scalp_challenges ||
          answersMap?.scalp_challenges ||
          'None',
      }
    : getMergedHairProfile(
        answersMap,
        agentData,
      )

  const hairProfileCanonical =
  answersMap?.hairProfileCanonical || {}

  const medicalContext = isGuestFlow
    ? {
        allergyOrMedical:
          answersMap?.medicalLifestyle
            ?.allergies_or_sensitivities ||
          answersMap?.medicalLifestyle
            ?.medical_conditions ||
          'No',

        pregnancyOrPostpartum:
          answersMap?.medicalLifestyle
            ?.pregnancy_or_postpartum ||
          'No',
      }
    : getMergedMedicalContext(
        answersMap,
        agentData,
      )

  const demographics = isGuestFlow
    ? {
        ageCategory:
          getAgeCategory(
            answersMap?.demographics?.age,
          ) || '18-24 years',

        gender:
          answersMap?.demographics?.gender ||
          'Female',

        residence: ['City'],

        placeName:
          answersMap?.demographics?.location ||
          'City',
      }
    : getMergedDemographics(
        answersMap,
        agentData,
      )

  const allergyOrMedical = normalizeYesNo(
    medicalContext.allergyOrMedical,
  )

  const pregnancyOrPostpartum = normalizeYesNo(
    medicalContext.pregnancyOrPostpartum,
  )

  const collectedAnswersList = Array.isArray(collectedAnswers)
    ? collectedAnswers
    : []

  /*
   * A photo is optional for the returning-user update flow.
   *
   * If the user happened to retake the analysis, use today's photo.
   * Otherwise send null.
   */
  const hairPhotoUrl =
    collectedAnswersList.find(
      (answer) => answer.stepId === 'hair_photo',
    )?.photoUrl || null

  return {
    language: apiLanguage,

    keepalive: true,

    signature: agentData?.signature || null,

    image: hairPhotoUrl,

    answers: [
      [finalHairClass],

      toFlatList(
        hairProfileCanonical.hair_state ||
          hairProfile.hair_state,
      ),

      toFlatList(
        hairProfileCanonical.hair_goals ||
          hairProfile.hair_goals,
      ),

      toFlatList(
        hairProfileCanonical.hair_challenges ||
          hairProfile.hair_challenges,
      ),

      toFlatList(
        hairProfileCanonical.scalp_challenges ||
          hairProfile.scalp_challenges,
      ),
    ],

    contraindications: [
      toFlatList(
        demographics.ageCategory,
      ),

      [allergyOrMedical],

      [pregnancyOrPostpartum],
    ],

    constraints: [
      toFlatList(
        demographics.residence,
      ),

      toFlatList(
        demographics.gender,
      ),

      toFlatList(
        demographics.ageCategory,
      ),

      [allergyOrMedical],

      [pregnancyOrPostpartum],
    ],

    encoded_category: 'None',

    env: 'Prd',

    is_trial: 'True',

    num_recommendations: 3,

    store_id: storeId,

    agent_id: agentId,

    place_name: demographics.placeName,
  }
}

export const buildRoutineAdvicePayload = ({
  recommendationPayload,
  selectedProduct,
  diagnosticsLLMText,
  language,
}) => {
  const normalizedLanguage = String(
    language || 'EN',
  )
    .trim()
    .toLowerCase()

  const apiLanguage = [
    'en',
    'fr',
    'nl',
    'sw',
  ].includes(normalizedLanguage)
    ? normalizedLanguage
    : 'en'
  return {
      language: apiLanguage,

      answers:
        recommendationPayload?.answers,

      constraints:
        recommendationPayload?.constraints,

      place_name:
        recommendationPayload?.place_name,

      env: 'Prd',
      is_trial: 'True',

      product_brand:
        selectedProduct?.brand,

      product_name:
        selectedProduct?.name,

      product_category:
        selectedProduct?.category,

      product_ingredients:
        selectedProduct?.ingredients,

      product_url:
        selectedProduct?.productUrl,

      product_quantity:
        selectedProduct?.quantity,

      product_description:
        selectedProduct?.description,

      llm_output:
        diagnosticsLLMText,
    }
  }
export const buildMetadataSubmissionPayload = ({
  recommendationPayload,
  selectedProduct,
  diagnosticsLLMText,
  productRoutineData,
  agentId,
  timestamp,
  agentData,
  consentData,
  phoneNumber,
  contactSource,
  productPrice,
  hairMask,
}) => {
  const jsonRecord = {
    language: recommendationPayload?.language || 'en',
    answers: recommendationPayload?.answers,
    constraints: recommendationPayload?.constraints,
    place_name: recommendationPayload?.place_name,
    product_brand: selectedProduct?.brand,
    product_name: selectedProduct?.name,
    product_category: selectedProduct?.category,
    product_ingredients: selectedProduct?.ingredients,
    product_url: selectedProduct?.productUrl,
    product_quantity: selectedProduct?.quantity,
    product_description: selectedProduct?.description,
    diagnostic_llm: diagnosticsLLMText,
    routine_llm: productRoutineData,
    agent_id: agentId,
    timestamp,
    contactSource: contactSource || null,
    photo_analysis_consent: consentData?.photoAnalysisConsent ?? false,
    phone_number: phoneNumber || null,
    model_training_consent: consentData?.modelTrainingConsent ?? false,
    product_price: selectedProduct?.price ?? null,
    hair_mask: hairMask || null,
  }

  return {
    record: jsonRecord,
    env: 'Prd',
    is_trial: 'True',
  }
}
